import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LINES = [
  { type: 'cmd', text: '> cliniq init --institution "your-college"' },
  { type: 'ok', text: '✓ Attendance & Sessions', delay: 600 },
  { type: 'ok', text: '✓ Internal Assessments', delay: 400 },
  { type: 'ok', text: '✓ Clinical Postings', delay: 350 },
  { type: 'ok', text: '✓ CRMI Rotations', delay: 300 },
  { type: 'ok', text: '✓ Elective Management', delay: 350 },
  { type: 'ok', text: '✓ Faculty Management', delay: 300 },
  { type: 'ok', text: '✓ Feedback & Surveys', delay: 350 },
  { type: 'ok', text: '✓ Wellness & Discipline', delay: 300 },
  { type: 'ok', text: '✓ Early Warning', delay: 350 },
  { type: 'ok', text: '✓ Outreach & Activities', delay: 300 },
  { type: 'empty', text: '', delay: 400 },
  { type: 'result', text: '→ 10 modules configured. Platform operational.', delay: 200 },
  { type: 'cmd', text: '> cliniq status', delay: 800 },
  { type: 'info', text: '  Departments: 34 | Levels: 7 | Roles: 6', delay: 400 },
  { type: 'info', text: '  Status: all systems operational', delay: 300 },
];

function TerminalTypewriter() {
  const [visibleLines, setVisibleLines] = useState([]);
  const [typing, setTyping] = useState('');
  const [lineIdx, setLineIdx] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (lineIdx >= LINES.length) {
      const timer = setTimeout(() => { setVisibleLines([]); setTyping(''); setLineIdx(0); }, 4000);
      return () => clearTimeout(timer);
    }
    const line = LINES[lineIdx];
    const delay = line.delay || 100;

    if (line.type === 'cmd') {
      let charIdx = 0;
      setTyping('');
      const charTimer = setInterval(() => {
        charIdx++;
        setTyping(line.text.slice(0, charIdx));
        if (charIdx >= line.text.length) {
          clearInterval(charTimer);
          setTimeout(() => { setVisibleLines((p) => [...p, line]); setTyping(''); setLineIdx((i) => i + 1); }, 300);
        }
      }, 35);
      return () => clearInterval(charTimer);
    } else if (line.type === 'empty') {
      const timer = setTimeout(() => { setVisibleLines((p) => [...p, line]); setLineIdx((i) => i + 1); }, delay);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => { setVisibleLines((p) => [...p, line]); setLineIdx((i) => i + 1); }, delay);
      return () => clearTimeout(timer);
    }
  }, [lineIdx]);

  useEffect(() => { if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight; }, [visibleLines, typing]);

  const colorMap = { cmd: 'text-white/80', ok: 'text-emerald-400', result: 'text-amber-300 font-semibold', info: 'text-white/50', empty: '' };

  return (
    <div ref={containerRef} className="h-full overflow-hidden px-5 py-4 space-y-[3px]">
      <AnimatePresence>
        {visibleLines.map((line, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.15 }} className={`text-[13px] font-mono leading-relaxed ${colorMap[line.type] || 'text-white/60'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {line.type === 'ok' ? <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 12 }}>{line.text}</motion.span> : line.text}
          </motion.div>
        ))}
      </AnimatePresence>
      {typing && <div className="text-[13px] font-mono text-white/80 leading-relaxed" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{typing}<span className="inline-block w-[7px] h-[15px] bg-emerald-400/80 ml-0.5 animate-pulse align-middle" /></div>}
      {!typing && lineIdx < LINES.length && <div className="text-[13px] font-mono text-white/40" style={{ fontFamily: 'JetBrains Mono, monospace' }}><span className="inline-block w-[7px] h-[15px] bg-white/30 animate-pulse align-middle" /></div>}
    </div>
  );
}

export default function RightTerminal() {
  return (
    <div className="relative">
      <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#DDE8E1] via-transparent to-[#D8E0D8] opacity-50" />
      <div className="relative rounded-[1.75rem] border border-stone-200/80 overflow-hidden shadow-[0_30px_80px_rgba(28,48,39,0.12)] bg-[#0c1a14]">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.06] bg-white/[0.03]">
          <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-400/60" /><div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" /><div className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" /></div>
          <span className="text-[10px] text-white/25 ml-2 font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>cliniq-360 — platform init</span>
        </div>
        <div className="h-[395px]"><TerminalTypewriter /></div>
      </div>
    </div>
  );
}
